import { View } from "@pimo/pimo-app-builder";
import {
  DragAndDropLayout,
  PMBarChartCard,
  PMDonutChartCard,
} from "@pimo/pimo-components";
import { ReportingItem } from "vmd-types";

import { TestAppState } from "../../../app";
import { DONUT_CARD_LAYOUT, DONUT_CHART_COLORS } from "../../../constants";
import { withTotalInTitle } from "../with-total-in-title";

const getLifeCycleData = (items: ReportingItem[] | undefined) => {
  const lifecycleCounts = new Map<string, number>();

  for (const item of items ?? []) {
    const lifecycle = item.basicItem?.lifeCycle;
    if (!lifecycle) continue;
    lifecycleCounts.set(lifecycle, (lifecycleCounts.get(lifecycle) ?? 0) + 1);
  }

  const entries = Array.from(lifecycleCounts.entries()).map(
    ([label, count], index) => ({
      label,
      count,
      color: DONUT_CHART_COLORS[index % DONUT_CHART_COLORS.length],
    })
  );

  return { entries };
};

/** Adds the life-cycle bar chart and donut chart (#2222). */
export function addLifeCycleCards(view: View<TestAppState, DragAndDropLayout>) {
  // Life Cycle
  const lifeCycleBarChartComponent = view.addComponent({
    component: PMBarChartCard,
    layoutProps: {
      componentId: "lifeCycleBarChartComponent",
      labels: ["barChart", "chart", "lifeCycle"],
      description: "Shows systems grouped by life cycle status.",
      title: "Systems per Life Cycle",
    },
  });

  lifeCycleBarChartComponent.mapState(({ currentReport }) => {
    const items = (currentReport?.project?.reportingItems ?? []).filter(
      (item) => !item?.isChannelHeader
    );
    const { entries } = getLifeCycleData(items);

    return {
      title: "Systems per Life Cycle",
      categories: entries.map((entry) => entry.label),
      data: entries.map((entry) => entry.count),
      chartType: 1 as const,
      xLabel: "Systems",
      yLabel: "Life Cycle",
      colors: entries.map((entry) => entry.color),
      fixedHeight: "320px",
    };
  });

  const lifeCycleDonutChartComponent = view.addComponent({
    component: PMDonutChartCard,
    layoutProps: {
      componentId: "lifeCycleDonutChartComponent",
      labels: ["donutChart", "chart", "lifeCycle"],
      description: "Shows systems grouped by life cycle status.",
      title: "Systems per Life Cycle",
    },
  });

  lifeCycleDonutChartComponent.mapState(({ currentReport }) => {
    const items = (currentReport?.project?.reportingItems ?? []).filter(
      (item) => !item?.isChannelHeader
    );
    const { entries } = getLifeCycleData(items);

    return {
      title: withTotalInTitle("Systems per Life Cycle", items.length),
      icon: "DonutLarge",
      ...DONUT_CARD_LAYOUT,
      data: entries.map((entry) => ({
        label: entry.label,
        value: entry.count,
        color: entry.color,
      })),
    };
  });
}
